/*
 * Hover color guard for Variator (EX Show Variants on Collection) cards.
 *
 * The theme's "second image on hover" can show a DIFFERENT color than the
 * card's variant (the app reuses the product's global second image). This fix
 * keeps the hover only when it is the SAME color, swaps it to the correct
 * same-color photo when one exists, and removes it when the color has a single
 * photo.
 *
 * Same-color detection: product media are grouped in color blocks (each block
 * starts at a variant's featured image and runs until the next color's featured
 * image). Read from /products/{handle}.js so it also works under filters.
 *
 * Robust timing: runs on initial load, on every DOM change (MutationObserver),
 * on window load, AND as a guaranteed backstop the moment a card is hovered
 * (when the card is definitely fully rendered). A card is only marked as done
 * once it was evaluated with a resolved main image, so a mistimed early pass
 * never "locks in" a wrong hover.
 *
 * No hardcoded list. No setTimeout, no var, no IIFE (per project rules).
 */
{
  const norm = (url) => {
    if (!url) return null;
    let path;
    try {
      path = new URL(url, window.location.href).pathname;
    } catch (e) {
      path = url;
    }
    return path
      .split('/')
      .pop()
      .split('?')[0]
      .replace(/_\d+x\d+(?=\.[a-z]+$)/i, '');
  };

  const sized = (src, width) => {
    const url = src.startsWith('//') ? `https:${src}` : src;
    return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
  };

  const productCache = new Map(); // handle -> Promise<{media, boundaries} | null>

  const getProduct = (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return null;
        const media = (data.media || []).map((m) => ({ file: norm(m.src), src: m.src }));
        const featured = new Set(
          (data.variants || [])
            .map((v) => norm(v.featured_image && v.featured_image.src))
            .filter(Boolean)
        );
        const boundaries = media
          .map((m, index) => (featured.has(m.file) ? index : -1))
          .filter((index) => index >= 0);
        return { media, boundaries };
      })
      .catch(() => null);

    productCache.set(handle, request);
    return request;
  };

  const blockStartOf = (pos, boundaries) => {
    let start = -1;
    for (const boundary of boundaries) {
      if (boundary <= pos) start = boundary;
      else break;
    }
    return start;
  };

  const nextBoundaryAfter = (start, boundaries, length) => {
    for (const boundary of boundaries) {
      if (boundary > start) return boundary;
    }
    return length;
  };

  const resolveCard = async (card) => {
    if (card.dataset.hoverGuard === 'done') return;

    const wrap = card.querySelector('.product-card__second-image');
    const link = card.querySelector('a[href]');
    const mainImg = card.querySelector('.product-card__image');
    const secondImg = wrap && wrap.querySelector('img');

    // nothing to guard here
    if (!wrap || !link || !mainImg || !secondImg) {
      card.dataset.hoverGuard = 'done';
      return;
    }

    const match = link.getAttribute('href').match(/\/products\/([^?#]+)/);
    if (!match) {
      card.dataset.hoverGuard = 'done';
      return;
    }

    // suppress the hover image right away so a wrong color never flashes
    hideHover(card);

    const data = await getProduct(match[1]);
    if (!card.isConnected) return;

    // no usable color grouping -> leave the hover as it is
    if (!data || data.boundaries.length < 2) {
      revealHover(wrap);
      card.dataset.hoverGuard = 'done';
      return;
    }

    const mainPos = data.media.findIndex((m) => m.file === norm(mainImg.currentSrc || mainImg.src));
    // main image not resolved yet -> do NOT lock the card, retry on a later pass/hover
    if (mainPos < 0) return;

    const start = blockStartOf(mainPos, data.boundaries);
    if (start < 0) {
      revealHover(wrap);
      card.dataset.hoverGuard = 'done';
      return;
    }
    const end = nextBoundaryAfter(start, data.boundaries, data.media.length);
    const hoverPos = data.media.findIndex((m) => m.file === norm(secondImg.currentSrc || secondImg.src));

    if (hoverPos >= start && hoverPos < end) {
      // already same color -> keep
      revealHover(wrap);
    } else {
      let secondPos = -1;
      for (let p = start; p < end; p += 1) {
        if (p !== mainPos) {
          secondPos = p;
          break;
        }
      }
      if (secondPos >= 0) {
        secondImg.removeAttribute('sizes');
        secondImg.removeAttribute('srcset');
        secondImg.src = sized(data.media[secondPos].src, 900);
        revealHover(wrap);
      } else {
        wrap.remove();
      }
    }

    card.dataset.hoverGuard = 'done';
  };

  // hide the hover image while we decide (prevents a wrong-color flash on hover)
  const hideHover = (card) => {
    if (card.dataset.hoverGuard === 'done') return;
    const wrap = card.querySelector('.product-card__second-image');
    if (wrap && !wrap.dataset.guardHidden) {
      wrap.dataset.guardHidden = '1';
      wrap.style.opacity = '0';
    }
  };

  const revealHover = (wrap) => {
    if (wrap && wrap.dataset.guardHidden) {
      wrap.style.opacity = '';
      delete wrap.dataset.guardHidden;
    }
  };

  const scan = () => {
    document.querySelectorAll('.product-card').forEach((card) => {
      if (card.dataset.hoverGuard !== 'done') resolveCard(card);
    });
  };

  const init = () => {
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        scan();
      });
    };

    schedule();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('load', schedule);

    // guaranteed backstop: fix a card the moment it is hovered
    document.addEventListener(
      'mouseover',
      (event) => {
        const card = event.target.closest && event.target.closest('.product-card');
        if (card && card.dataset.hoverGuard !== 'done') {
          hideHover(card);
          resolveCard(card);
        }
      },
      true
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}